<script setup lang="ts">
import {
  NConfigProvider,
  NDialogProvider,
  NMessageProvider,
  NNotificationProvider,
  darkTheme,
  lightTheme,
  enUS,
} from 'naive-ui'
import { GlobalLoading } from '@/components/GlobalLoading'
import { isMobile } from '@/utils'
import { throttle } from 'lodash-es'
import { setAppScrollTarget } from '@/utils/scroll-lock-manager'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

const { listenForMessages, requestNotificationPermission } = usePushManager()
const { initInvitorDomain, authCode2UserInfo, tgLogin, initTGParams } = useLogin()
const { isTelegram, telegramWebApp } = useTelegram()
const route: RouteLocationNormalizedLoaded = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { isLogin, messagePlacement } = storeToRefs(userStore)
const isDark = useDark()

// 页面性能指标收集
const pageLoadTime = ref(0)
const resourcesLoaded = ref(false)
const scrollbarWidth = window.innerWidth - document.body.clientWidth

const outlineContactSupportRight = computed(() => {
  const isWin = navigator.platform?.startsWith('Win')
  const hasScrollbar =
    document.body.scrollHeight > (window.innerHeight || document.documentElement.clientHeight)
  return !isMobile() && hasScrollbar && isWin ? scrollbarWidth : 0
})

const configProviderRef = ref()
const notificationRef = ref()
const themeOverrides = useThemeOverrides(isDark)

type PlacementType =
  | 'bottom'
  | 'bottom-right'
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | undefined
  | any
const placement: PlacementType = computed(() => {
  return isMobile() ? 'bottom' : messagePlacement.value || 'bottom-right'
})
watch(
  isLogin,
  async (b: boolean) => {
    if (!b) {
      userStore.SET_MESSAGESTATUS({
        isShowMessage: false,
        isReadState: false,
      })
    }
  },
  {
    immediate: true,
  },
)

const jump = async () => {
  try {
    const res = await baseService.profileInfo({
      addressOrDomain: import.meta.env.VITE_APP_DFANS_DOMAIN,
    })
    router.push({
      name: 'messageDetail',
      params: {
        privateChatId: res?.privateChatId,
      },
      query: {
        privateChatId: res?.privateChatId,
        address: res?.address,
      },
    })
  } catch (err: any) {
    console.error(err)
  }
}

watch(route, () => {
  initInvitorDomain()
  const params = useUrlSearchParams('history')
  const params1 = useUrlSearchParams('hash')
  if (params?.authCode) {
    authCode2UserInfo(params?.authCode as string)
  }
  if (params?.promotionTag || params1?.promotionTag) {
    reportService.addPromotionTagUsage({
      promotionTag: params?.promotionTag || params1?.promotionTag,
    })
  }
})

// 监听页面完全加载
window.addEventListener('load', () => {
  const loadTime = window.performance.now()
  pageLoadTime.value = loadTime
  resourcesLoaded.value = true
})

// 初始化应用
const initApp = async () => {
  try {
    await router.isReady()
    GlobalLoading.close()
  } catch (error) {
    console.error('初始化失败:', error)
    GlobalLoading.close()
  }
}

onMounted(() => {
  initApp()
  if (isTelegram) {
    telegramWebApp.expand() // 启动后全屏显示
    telegramWebApp.disableVerticalSwipes() // 禁用垂直滑动以关闭
    telegramWebApp.enableClosingConfirmation() // 在用户尝试关闭小程序时启用确认对话框

    if (!isLogin.value) tgLogin()
    else initTGParams()
  }
  initInvitorDomain()
  useColorMode({
    storageKey: 'vueuse-color-scheme',
    emitAuto: true,
    initialValue: 'dark',
    modes: { dark: 'dark', light: 'light' },
  })
  if (!isTelegram && isLogin.value && 'PushManager' in window) {
    listenForMessages()
    requestNotificationPermission()
  }
  useEventListener(
    document.body,
    'scroll',
    throttle(() => {
      const scrollTop = document.body.scrollTop || document.documentElement.scrollTop
      userStore.SET_SCROLLTOP(scrollTop)
    }, 100),
  )
  if (configProviderRef.value?.$el) {
    setAppScrollTarget(configProviderRef.value.$el)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', () => {})
})
</script>

<template>
  <NConfigProvider
    ref="configProviderRef"
    :theme="isDark ? darkTheme : lightTheme"
    :theme-overrides="themeOverrides || {}"
    :locale="enUS"
  >
    <NMessageProvider :placement="placement" closable>
      <NNotificationProvider ref="notificationRef" placement="top">
        <NDialogProvider>
          <Bootstrap />
          <RouterView />
          <GlobalPluginBootstrap />
          <div
            v-if="isLogin && !['messageBoard', 'messageDetail'].includes(route.name as string)"
            @click="jump"
            class="hidden sm:flex fixed cursor-pointer bottom-15vh w-10.5 h-10.5 rounded-lt-1 rounded-lb-1 items-center justify-center brand-gradient"
            :style="{ right: `${outlineContactSupportRight}px` }"
          >
            <div class="text-2xl text-white" i-ic:outline-contact-support />
          </div>
        </NDialogProvider>
      </NNotificationProvider>
    </NMessageProvider>
  </NConfigProvider>
</template>

<style scoped>
.app-wrapper {
  min-height: 100vh;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.app-ready {
  opacity: 1;
}
</style>
